import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Calendar, Image, Film, ArrowRight, Palette } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5 } }) };

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/events`),
      axios.get(`${API}/event-types`),
    ]).then(([evRes, typesRes]) => {
      setEvents(evRes.data);
      setTypes(typesRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? events : events.filter(e => e.event_type === filter);

  return (
    <div data-testid="events-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Events</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Our <span className="gold-gradient-text">Event Coverage</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base max-w-xl">
              Browse our event galleries — photos, videos, and drone footage from every occasion we've captured. Leave your reviews and feedback to help us keep raising the bar.
            </motion.p>
          </motion.div>

          {/* Filters */}
          <div className="flex gap-2 mb-10 overflow-x-auto pb-2">
            <button data-testid="event-filter-all" onClick={() => setFilter("all")} className={`px-5 py-2 text-sm font-body font-light whitespace-nowrap transition-all ${filter === "all" ? "bg-gold text-black" : "border border-white/10 text-white/60 hover:border-white/30"}`}>
              All Events
            </button>
            {types.map(t => (
              <button key={t} data-testid={`event-filter-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(t)} className={`px-5 py-2 text-sm font-body font-light whitespace-nowrap transition-all ${filter === t ? "bg-gold text-black" : "border border-white/10 text-white/60 hover:border-white/30"}`}>
                {t}
              </button>
            ))}
          </div>

          {/* Featured: Liguanea Art Festival (we feature, we don't host) */}
          <Link to="/liguanea" data-testid="events-liguanea-feature"
            className="block mb-10 group overflow-hidden border border-gold/30 hover:border-gold/60 bg-gradient-to-br from-luxury-black via-[#1a0f00] to-luxury-black transition-colors">
            <div className="grid grid-cols-1 md:grid-cols-3">
              <div className="md:col-span-2 p-6 md:p-8">
                <p className="text-[10px] font-body uppercase tracking-[0.3em] text-gold mb-2 inline-flex items-center gap-1.5">
                  <Palette size={11} /> Featured · We cover this
                </p>
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">
                  Liguanea <span className="italic text-gold">Art Festival</span>
                </h3>
                <p className="text-white/60 font-body text-sm max-w-xl">
                  An annual Caribbean showcase of visual art, live performance, and photography
                  in the heart of Liguanea, Kingston. We provide premium event coverage —
                  Aristocrats does not host the festival.
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-gold text-xs font-body uppercase tracking-wider group-hover:gap-2 transition-all">
                  Explore the festival page <ArrowRight size={12} />
                </span>
              </div>
              <div className="hidden md:flex items-center justify-center p-8 bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_60%)]">
                <Palette className="text-gold/40" size={96} strokeWidth={0.7} />
              </div>
            </div>
          </Link>

          {loading ? <div className="text-gold animate-pulse text-sm font-body">Loading events...</div> : filtered.length === 0 ? (
            <div className="glass-card p-16 text-center">
              <Calendar size={48} className="text-gold/30 mx-auto mb-4" />
              <h3 className="text-white font-heading text-xl mb-2">No Events Yet</h3>
              <p className="text-white/40 font-body text-sm">Check back soon for our latest event coverage.</p>
            </div>
          ) : (
            <motion.div initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((ev, i) => (
                <motion.div key={ev.id} variants={fadeUp} custom={i}>
                  <Link to={`/events/${ev.id}`} data-testid={`event-card-${ev.id}`} className="glass-card overflow-hidden group block hover:border-gold/30 transition-all">
                    {ev.cover_image && (
                      <div className="aspect-[16/10] overflow-hidden bg-luxury-surface relative">
                        <img src={ev.cover_image} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                        <div className="absolute top-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-sm text-gold text-[10px] font-body uppercase tracking-wider">{ev.event_type}</div>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-white font-heading text-lg group-hover:text-gold transition-colors mb-2">{ev.title}</h3>
                      {ev.description && <p className="text-white/40 font-body text-xs leading-relaxed mb-4 line-clamp-2">{ev.description}</p>}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-white/30 text-xs font-body">
                          {ev.event_date && <span className="flex items-center gap-1"><Calendar size={12} /> {ev.event_date}</span>}
                          {ev.location && <span className="flex items-center gap-1"><MapPin size={12} /> {ev.location}</span>}
                        </div>
                        <div className="flex items-center gap-3 text-white/30 text-xs font-body">
                          <span className="flex items-center gap-1"><Image size={12} /> {ev.media_count}</span>
                          {ev.avg_rating > 0 && <span className="flex items-center gap-1"><Star size={12} className="text-gold fill-gold" /> {ev.avg_rating}</span>}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
